import { yupResolver } from '@hookform/resolvers/yup';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from '@mui/lab';

import {
  Autocomplete,
  Box,
  FormControl,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import FormatCurrencyInput from '../../components/FormatNumber/FormatCurrencyInput';
import LocationInput from '../../components/LocationInput/LocationInput';
import { TalentType } from '../../enums/talentType';
import { QuickPayment } from '../../models/quickPayment';
import { Talent } from '../../models/talent';
import FormHelperText from '@mui/material/FormHelperText';

interface ValidationError {
  errors: string | Array<string>;

  value: string;

  path: string;

  inner?: Array<ValidationError>;
}

interface Props {
  item: QuickPayment;
  index: number;
  talents: Talent[];
  duplicates: QuickPayment[];
  forwardRef: React.RefObject<HTMLElement>;
  checkDuplicates: () => void;
  remoteValidate: boolean;
  duplicatedRows: QuickPayment[];
  rows: QuickPayment[];
  parentErrors: ValidationError[];
  setRows: (e: QuickPayment[]) => void;
}

const schema = yup.object({
  id: yup.number().nullable(),
  poNumber: yup.number().nullable(),
  paymentDate: yup
    .string()
    .required('Payment Date is required')
    .typeError('Payment Date is required'),
  talent: yup.object().required('Talent is required'),
  description: yup
    .string()
    .required('Description is required')
    .typeError('Description is required'),
  category: yup
    .string()
    .required('Category is required')
    .typeError('Category is required'),
  expenseCode: yup.string().nullable(),
  payType: yup.string().nullable(),
  quantity: yup.number().nullable(),
  hourlyRate: yup.number().nullable(),
  totalAmount: yup
    .string()
    .required('Amount is required')
    .typeError('Amount is required')
});

const PaymentRow = (props: Props): JSX.Element => {
  const [, setAddressHelper] = useState('');
  const [locationWidth, setLocationWidth] = useState('155px');
  const [talentWidth, setTalentWidth] = useState('180px');
  const [isVisible, setVisible] = useState(false);
  const ref = useRef(null);

  const observerCallback = (entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    setVisible(entry.isIntersecting);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: '450px'
    });
    if (ref.current) observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  });

  const {
    control,
    watch,
    reset,
    setValue,
    register,
    trigger,
    formState: { errors }
  } = useForm<QuickPayment>({
    defaultValues: props.item,
    reValidateMode: 'onBlur',
    resolver: yupResolver(schema),
    mode: 'onBlur'
  });

  const quantity = watch('quantity');
  const hourlyRate = watch('hourlyRate');
  const payType = watch('payType');
  const address = watch('address');
  const talent = watch('talent');
  const totalAmount = watch('totalAmount');
  const paymentDate = watch('paymentDate');

  useEffect(() => {
    const errorsAux = props.parentErrors.filter((element: ValidationError) =>
      element.path.includes(`[${props.index}]`)
    );
    if (errorsAux.length > 0) {
      trigger();
    }
  }, [
    props.parentErrors,
    props.duplicatedRows,
    talent,
    totalAmount,
    paymentDate
  ]);

  watch((data) => {
    const aux: QuickPayment[] = props.rows;
    aux[props.index] = data as QuickPayment;
    props.setRows(aux);
  });

  useEffect(() => {
    reset(props.item);
    if (props?.item?.address) {
      setAddressHelper(props?.item?.address);
    } else {
      setAddressHelper('');
    }
    if (props?.item?.talent) {
      setValue('talent', props?.item?.talent);
    }
  }, [props.item]);

  useEffect(() => {
    if (hourlyRate && quantity) {
      const hourlyRateFormat = hourlyRate.toString().replaceAll('$', '');
      setValue('hourlyRate', Number(hourlyRateFormat));
      setValue('totalAmount', Number(hourlyRateFormat) * quantity);
    }
  }, [hourlyRate, quantity]);

  useEffect(() => {
    if (talent) {
      setValue('payType', TalentType[talent.accountType]);
    }
  }, [payType]);

  return (
    <>
      <TableRow ref={ref} key={props.index} hover tabIndex={-1}>
        <TableCell sx={{ px: '10px', py: 3 }}>
          <TextField
            sx={{ width: '54px !important' }}
            size="small"
            {...register('poNumber')}
          />
        </TableCell>
        {isVisible && (
          <>
            <TableCell sx={{ px: '10px', py: 3 }}>
              <Controller
                name="paymentDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    renderInput={(params) => (
                      <TextField
                        error={true}
                        helperText={errors.paymentDate?.message}
                        sx={{
                          width: '140px !important',
                          fieldset: {
                            borderColor: errors.paymentDate
                              ? 'red !important'
                              : ''
                          },
                          svg: {
                            padding: '0px',
                            width: '15px',
                            height: '20px'
                          }
                        }}
                        size="small"
                        {...params}
                      />
                    )}
                    {...field}
                  />
                )}
              />
            </TableCell>
            <TableCell sx={{ px: '10px', py: 3 }}>
              <Box sx={{ position: 'relative' }}>
                <Controller
                  name="talent"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      options={props.talents}
                      getOptionLabel={(option) => {
                        if (option) {
                          return `${option?.firstName || ''} ${
                            option?.lastName || ''
                          } - ${option?.id || ''}`;
                        }
                        return '';
                      }}
                      onFocus={() => setTalentWidth('300px')}
                      onBlur={() => setTalentWidth('180px')}
                      filterSelectedOptions
                      disableClearable
                      value={value ?? undefined}
                      onChange={(e, data) => {
                        setValue('payType', TalentType[data.accountType]);
                        setValue('userId', data.id);
                        onChange(data);
                        props.checkDuplicates();
                      }}
                      ChipProps={{
                        sx: {
                          background: '#CAE4FF',
                          color: 'primary.main',
                          zIndex: '3'
                        }
                      }}
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          // multiline
                          error={
                            !!errors.talent ||
                            props.duplicates.find((obj: QuickPayment) => {
                              return obj.talent == talent;
                            }) != undefined
                          }
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            width: talentWidth + ' !important',
                            transition: 'width 0.7s, margin 0.7s , height 0.7s',
                            marginRight: talentWidth == '300px' ? '-155px' : '',
                            zIndex: 10,
                            background: talentWidth == '300px' ? 'white' : '',
                            pr: '5px',
                            button: {
                              ml: '15px',
                              width: '20px',
                              height: '20px'
                            },
                            '& .MuiAutocomplete-endAdornment': {
                              position: 'absolute',
                              right: '1px !important'
                            }
                          }}
                          {...params}
                        />
                      )}
                    />
                  )}
                />
                {props.duplicates.includes(props.item) &&
                  !props.duplicatedRows.includes(props.item) && (
                    <Typography
                      sx={{
                        color: '#ffc107',
                        position: 'absolute',
                        top: '100%'
                      }}
                      variant="caption"
                    >
                      Duplicated name, check ID
                    </Typography>
                  )}
                {props.duplicatedRows.includes(props.item) && (
                  <Typography
                    sx={{
                      color: 'red',
                      position: 'absolute',
                      top: '100%'
                    }}
                    variant="caption"
                  >
                    Duplicated row
                  </Typography>
                )}
              </Box>
            </TableCell>
            <TableCell sx={{ px: '10px', py: 3 }}>
              <TextField
                sx={{ width: '200px !important' }}
                size="small"
                multiline
                maxRows={3}
                error={!!errors.description}
                helperText={errors.description?.message}
                onBlurCapture={() => props.checkDuplicates()}
                {...register('description')}
              />
            </TableCell>
            <TableCell sx={{ px: '10px', py: 3 }}>
              <FormControl fullWidth>
                <Select
                  sx={{
                    width: '130px !important',
                    '& .MuiAutocomplete-endAdornment': {
                      position: 'absolute',
                      right: '1px !important'
                    }
                  }}
                  {...register('category')}
                  defaultValue={props.item.category || ''}
                  onBlurCapture={() => props.checkDuplicates()}
                  error={!!errors.category}
                  size="small"
                >
                  <MenuItem value="" disabled></MenuItem>
                  <MenuItem value={'Payment'}>Payment </MenuItem>
                  <MenuItem value={'Expense'}>Expense</MenuItem>
                </Select>
                <FormHelperText>{errors.category?.message}</FormHelperText>
              </FormControl>
            </TableCell>
            <TableCell sx={{ px: '10px', py: 3 }}>
              <TextField
                sx={{ width: '90px !important' }}
                size="small"
                onBlurCapture={() => props.checkDuplicates()}
                {...register('expenseCode')}
              />
            </TableCell>
            <TableCell sx={{ px: '10px', py: 3 }}>
              <TextField
                sx={{
                  width: '70px !important',
                  transition: 'width 0.7s'
                }}
                size="small"
                disabled
                {...register('payType')}
              />
            </TableCell>
          </>
        )}
        <TableCell sx={{ px: '10px', py: 3, opacity: isVisible ? 1 : 0 }}>
          <Box
            sx={{
              position: 'relative',
              width: '155px'
            }}
          >
            <LocationInput
              id="address"
              name={''}
              value={address}
              multiline
              maxRows={2}
              size="small"
              sx={{
                width: locationWidth + ' !important',
                transition: 'width 0.7s, margin 0.7s , height 0.7s',
                marginRight: locationWidth == '300px' ? '-155px' : '',
                zIndex: 10,
                background: locationWidth == '300px' ? 'white' : ''
              }}
              onFocus={() => setLocationWidth('300px')}
              onBlur={() => setLocationWidth('155px')}
              onChangeLocation={(address) => {
                setValue('address', address);
              }}
            />
          </Box>
        </TableCell>

        {isVisible && (
          <>
            <TableCell sx={{ px: '10px', py: 3 }}>
              <Controller
                name="quantity"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    sx={{
                      width:
                        locationWidth == '300px' ? '40px' : '70px !important',
                      paddingLeft: locationWidth == '300px' ? '55px' : '',
                      transition: 'width 0.7s, padding 0.7s'
                    }}
                    value={value ? value : null}
                    onBlurCapture={() => props.checkDuplicates()}
                    onChange={(e) => {
                      onChange(Number(e.target.value));
                      if (hourlyRate && Number(e.target.value)) {
                        setValue(
                          'totalAmount',
                          Number(e.target.value) * hourlyRate
                        );
                      }
                      props.checkDuplicates();
                    }}
                    size="small"
                  />
                )}
              />
            </TableCell>
            <TableCell sx={{ px: '10px', py: 3 }}>
              <Controller
                name="hourlyRate"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    name="hourlyRate"
                    value={value ? value : null}
                    sx={{
                      width:
                        locationWidth == '300px' ? '90px' : '100px !important',
                      paddingLeft: locationWidth == '300px' ? '40px' : '',
                      transition: 'width 0.7s, padding 0.7s'
                    }}
                    size="small"
                    onBlurCapture={() => props.checkDuplicates()}
                    onChange={(e) => {
                      onChange(Number(e.target.value));
                      if (Number(e.target.value) && quantity) {
                        setValue(
                          'totalAmount',
                          quantity * Number(e.target.value)
                        );
                      }
                    }}
                    InputProps={{
                      // eslint-disable-next-line
                      inputComponent: FormatCurrencyInput as any
                    }}
                  />
                )}
              />
            </TableCell>
            <TableCell sx={{ px: '10px', py: 3 }}>
              <Controller
                name="totalAmount"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    name="totalAmount"
                    value={value ? value : null}
                    error={!!errors.totalAmount}
                    helperText={errors.totalAmount?.message}
                    sx={{ width: '100px !important' }}
                    size="small"
                    disabled={!!quantity || !!hourlyRate}
                    onKeyDown={() => {
                      setValue('quantity', null);
                      setValue('hourlyRate', null);
                    }}
                    onChange={(e) => onChange(Number(e.target.value))}
                    InputProps={{
                      // eslint-disable-next-line
                      inputComponent: FormatCurrencyInput as any
                    }}
                  />
                )}
              />
            </TableCell>
            <TableCell sx={{ px: '10px', py: 3 }}>
              <Box sx={{ display: 'flex' }}>
                <Tooltip title="Duplicate">
                  <ContentCopyIcon
                    sx={{
                      mr: 1
                    }}
                    onClick={() => {
                      const aux = [...props.rows];
                      aux.splice(props.index + 1, 0, {
                        ...props.rows[props.index]
                      });
                      props.setRows(aux);
                    }}
                  />
                </Tooltip>
                <Tooltip title="Delete">
                  <DeleteIcon
                    onClick={() => {
                      const aux = [...props.rows];
                      aux.splice(props.index, 1);
                      props.setRows([...aux]);
                    }}
                  />
                </Tooltip>
              </Box>
            </TableCell>
          </>
        )}
      </TableRow>
    </>
  );
};
export default PaymentRow;
